import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DateFieldInput, ErrorHelperText, PrimaryButton, Space } from '@/components';
import { useDimensions, useStateMountSafe } from '@/hooks';
import { container } from '@/utils/ui';

import { TransactionFilterCustomPeriodInputProps, TransactionFilterPeriod } from './types';

const TransactionFilterCustomPeriodInput: React.FC<TransactionFilterCustomPeriodInputProps> = ({
  value,
  onSelect,
  'data-test': dataTest,
}) => {
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useStateMountSafe<Partial<TransactionFilterPeriod>>(
    () =>
      value || {
        dateFrom: moment()
          .startOf('day')
          .subtract(1, 'month')
          .toDate(),
        dateTo: moment()
          .endOf('day')
          .toDate(),
      },
  );
  const [ref, dimension] = useDimensions();
  const isMultiline = useMemo(() => dimension && dimension.width <= container.mobileSwitchWidth, [dimension]);
  const [toLargePeriod, setPeriodIsToLarge] = useStateMountSafe<boolean>();
  const [hasError, setHasError] = useStateMountSafe<boolean>(false);

  const onChange = useCallback(
    (v: Partial<TransactionFilterPeriod>) => {
      setSelected((oldValue) => {
        const newValue = { ...oldValue, ...v };
        setPeriodIsToLarge(
          // prettier-ignore
          // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
          !!newValue.dateTo
          && !!newValue.dateFrom
          && moment(newValue.dateTo)
            .subtract(1, 'year')
            .subtract(1, 'day')
            .isAfter(newValue.dateFrom),
        );
        return newValue;
      });
    },
    [setPeriodIsToLarge, setSelected],
  );

  const isSubmitDisabled = useMemo(
    () =>
      // prettier-ignore
      // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
      !selected.dateFrom
      || Number.isNaN(selected.dateFrom.getDate())
      || !selected.dateTo
      || Number.isNaN(selected.dateTo.getDate())
      || hasError
      || toLargePeriod,
    [hasError, selected.dateFrom, selected.dateTo, toLargePeriod],
  );

  const doSubmit = useCallback(async () => {
    if (!selected?.dateFrom || !selected?.dateTo || isSubmitDisabled) {
      return;
    }
    await onSelect({ ...selected, dateFrom: selected.dateFrom, dateTo: selected.dateTo });
  }, [isSubmitDisabled, onSelect, selected]);

  return (
    <Space
      direction="vertical"
      size={2}
      style={{
        alignItems: 'center',
        alignSelf: 'center',
        paddingLeft: 10,
        paddingRight: 10,
        ...(isMultiline ? { width: '100%' } : {}),
      }}
    >
      <Space style={isMultiline ? { width: '100%' } : {}} direction={isMultiline ? 'vertical' : 'horizontal'} ref={ref}>
        <DateFieldInput
          data-test={dataTest && `${dataTest}-from`}
          onChange={(dateFrom) =>
            onChange({
              dateFrom: dateFrom
                ? moment(dateFrom)
                    .startOf('day')
                    .toDate()
                : undefined,
            })
          }
          value={{ hasError: false, value: selected.dateFrom }}
          messages={{
            placeholder: formatMessage({ id: 'demo.pages.transactions.filter.select.custom.labels.from' }),
          }}
          onError={setHasError}
        />
        {!isMultiline && <span style={{ display: 'flex', alignSelf: 'center' }}>-</span>}
        <DateFieldInput
          data-test={dataTest && `${dataTest}-to`}
          disables={{ future: true }}
          onChange={(dateTo) =>
            onChange({
              dateTo: dateTo
                ? moment(dateTo)
                    .endOf('day')
                    .toDate()
                : undefined,
            })
          }
          value={{ hasError: false, value: selected.dateTo }}
          messages={{ placeholder: formatMessage({ id: 'demo.pages.transactions.filter.select.custom.labels.to' }) }}
          onError={setHasError}
        />
      </Space>
      {toLargePeriod && (
        <ErrorHelperText data-test={dataTest && `${dataTest}-error`}>
          <FormattedMessage id="demo.pages.transactions.filter.select.custom.error.toLarge" />
        </ErrorHelperText>
      )}
      <PrimaryButton
        data-test={dataTest && `${dataTest}-submit`}
        title={<FormattedMessage id="demo.pages.transactions.filter.select.custom.labels.submit" />}
        disabled={isSubmitDisabled}
        onClick={doSubmit}
      />
    </Space>
  );
};

export default React.memo(TransactionFilterCustomPeriodInput);
